import React, { memo, useEffect, useState } from 'react';
import { connect } from 'react-redux';

import CollapseComponent from '../components/GeneralForm/CollapseComponent';
import CustomBadge from '../components/GeneralForm/CustomBadge';
import PageComponent from '../components/PageComponent';
import CustomButton from '../components/GeneralForm/CustomButton';
import CustomColorPicker from '../components/GeneralForm/CustomColorPicker';
import { showMsg } from '../services/library';
import { saveThemeColors } from '../services/themeService';
import { getLightness, MODE_LIST, LIGHT_MODE } from '../generalUtils/themeClasses';
import { performThemeFn } from '../generalUtils/themeStyleTag';
import { LOADING } from '../constants/messageConst';
import { handleThemeArr } from '../containers/MainLayout/mainLayoutSlice';
import CustomCard from '../components/GeneralForm/CustomCard';
import CopyText from '../components/GeneralForm/CopyText';
import CustomRadioButton from '../components/GeneralForm/CustomRadioButton';

const ThemeDocs = ({ user, themeArr }) => {
  const { themeColorObj, baseThemeColorArr, mode, completeThemeSetArr } = themeArr;

  const [inputColor, setInputColor] = useState('#000000');
  const [matchedColor, setMatchedColor] = useState(null);
  const [changeColorArr, setChangeColorArr] = useState([]);
  const [appThemeSelection, setAppThemeSelection] = useState(mode);

  const hexToRGB = hex => {
    const bigint = parseInt(hex.slice(1), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return { r, g, b };
  };
  const colorDifference = (color1, color2) => {
    // Calculate color difference using Euclidean distance
    return Math.sqrt(
      Math.pow(color1.r - color2.r, 2) + Math.pow(color1.g - color2.g, 2) + Math.pow(color1.b - color2.b, 2)
    );
  };
  const findClosestColor = () => {
    let minDifference = Number.MAX_VALUE;
    let closestColor = null;
    const colorList = Object.values(themeColorObj);
    colorList.forEach(color => {
      const inputRGB = hexToRGB(inputColor);
      const listRGB = hexToRGB(color);
      const difference = colorDifference(inputRGB, listRGB);
      if (difference < minDifference) {
        minDifference = difference;
        closestColor = color;
      }
    });
    setMatchedColor(closestColor);
  };

  useEffect(() => {
    findClosestColor();
  }, [inputColor]);

  useEffect(() => {
    if (baseThemeColorArr && baseThemeColorArr.length > 0) {
      setChangeColorArr(baseThemeColorArr);
      setAppThemeSelection(mode);
    }
  }, [baseThemeColorArr]);

  const RenderColorPalette = () => {
    function findKeyByValue(obj, targetValue) {
      for (const key in obj) {
        if (obj.hasOwnProperty(key) && obj[key] === targetValue) {
          return key;
        }
      }
      return '';
    }
    const foundKey = findKeyByValue(themeColorObj, matchedColor);

    return (
      <>
        <div className="row mb-3 align-items-end">
          <CustomColorPicker
            value={inputColor}
            onChange={e => setInputColor(e.target.value)}
            className="ml-3 mr-3"
            label="Input Color"
          />

          <CustomColorPicker value={matchedColor} label="Matched Color" readOnly />
          {foundKey && <CopyText textClassName="pl-3" text={foundKey} />}
        </div>
        <div className="row ml-2 mr-2">
          {baseThemeColorArr
            ?.filter(d => d.theme_generateshade)
            ?.map(item => {
              const { theme_colorname } = item;

              const filterKeysByColor = colorName => {
                return Object.keys(themeColorObj).filter(
                  key => key === colorName || key.startsWith(`${colorName}-`)
                );
              };
              const grayAndDarkerKeys = filterKeysByColor(theme_colorname);
              const colorObjects = grayAndDarkerKeys?.map(key => ({
                theme_colorname: key,
                theme_colorvalue: themeColorObj[key]
              }));

              const elementTag = colorObjects?.map((x, index) => {
                const { theme_colorname, theme_colorvalue } = x;

                const fontColor = getLightness({ color: theme_colorvalue, mode });
                const isFirstChild = index === 0;
                const colorName = isFirstChild ? theme_colorname : theme_colorname.split('-')[1];

                return (
                  <div key={theme_colorname} className="text-center">
                    <div
                      style={{
                        width: '100%',
                        padding: 4,
                        paddingTop: isFirstChild ? 10 : 4,
                        paddingBottom: isFirstChild ? 10 : 4,
                        backgroundColor: `var(--${theme_colorname})`,
                        display: 'inline-block'
                      }}
                    >
                      <span className={`fs-14 ${!isFirstChild ? 'd-flex' : ''}`}>
                        <span
                          style={{
                            cursor: 'pointer',
                            color: fontColor,
                            fontWeight: isFirstChild ? 'bold' : 'normal',
                            width: isFirstChild ? '100%' : '30%'
                          }}
                          onClick={() => navigator.clipboard.writeText(theme_colorname)}
                        >
                          {colorName}
                        </span>
                        <CopyText
                          text={theme_colorvalue}
                          style={{
                            color: fontColor,
                            fontWeight: isFirstChild ? 'bold' : 'normal',
                            width: isFirstChild ? '100%' : '70%'
                          }}
                        />
                      </span>
                    </div>
                  </div>
                );
              });
              return (
                <div key={`div${theme_colorname}`} className="col-2 b-1 p-2">
                  {elementTag}
                </div>
              );
            })}
        </div>
      </>
    );
  };

  const RenderBadge = completeThemeSetArr?.map(item => {
    const { theme_colorname } = item;
    return (
      <CustomBadge
        className="mr-3"
        color={theme_colorname}
        title={theme_colorname}
        key={theme_colorname}
        label={theme_colorname}
      />
    );
  });
  const RenderButton = completeThemeSetArr?.map(item => {
    const { theme_colorname } = item;
    return (
      <CustomButton className="mr-3 mb-2" color={theme_colorname} key={theme_colorname}>
        {theme_colorname}
      </CustomButton>
    );
  });
  const RenderOutlineButton = completeThemeSetArr?.map(item => {
    const { theme_colorname } = item;
    return (
      <CustomButton className="mr-3 mb-2" outline color={theme_colorname} key={theme_colorname}>
        {theme_colorname}
      </CustomButton>
    );
  });

  const ThemeSet = () => {
    if (changeColorArr?.length === 0) {
      return <div className="p-3 text-info">{LOADING}</div>;
    }

    const saveThemeFn = async ({ data = [], reset = false }) => {
      const response = await saveThemeColors({
        themeArr: data.filter(d => d.theme_isvisible),
        user_id: user?.user_id,
        mode: appThemeSelection,
        isLightTheme: appThemeSelection === LIGHT_MODE,
        reset
      });
      if (response.status) {
        await performThemeFn({ user_id: user?.user_id });
        showMsg(response?.message, 'success');
      } else {
        showMsg('Not updating', 'error');
      }
    };

    const handleChangeColor = (value, theme_colorname) => {
      const data = baseThemeColorArr?.map(x => ({
        ...x,
        theme_colorvalue: x.theme_colorname === theme_colorname ? value : x.theme_colorvalue
      }));
      setChangeColorArr(data);
    };

    return (
      <>
        <CollapseComponent title="Mode" cardDataLoaded isOpen>
          <div className="row ml-1 align-items-center">
            <CustomRadioButton
              name="apptheme"
              options={MODE_LIST?.map(mode => ({
                value: mode.value,
                label: mode.label,
                checked: appThemeSelection === mode.value
              }))}
              className="mr-3"
              onChange={e => setAppThemeSelection(e.target.value)}
            />
            <CustomButton
              className="mr-3 mb-2"
              color="secondary"
              title="Reset Theme"
              onClick={() => {
                const resetColorArr = changeColorArr?.map(x => ({
                  ...x,
                  theme_colorvalue: x.theme_resetcolorvalue
                }));
                saveThemeFn({ data: resetColorArr, reset: true });
              }}
            >
              <i className="fa fa-refresh pr-1" aria-hidden="true" /> Reset Theme
            </CustomButton>
            <CustomButton
              title="Save Theme"
              className="mb-2"
              color="primary"
              onClick={() => saveThemeFn({ data: changeColorArr })}
            >
              <i className="fa fa-check-circle pr-1" aria-hidden="true" /> Save Theme
            </CustomButton>
          </div>

          {appThemeSelection === 'custom' && (
            <CustomCard isShowCard={false} title="Custom Theme" addedClass="p-3 m-0">
              <div className="row">
                {baseThemeColorArr
                  ?.filter(d => d.theme_isvisible)
                  ?.map((item, index) => {
                    const { theme_colorname, theme_resetcolorvalue } = item;

                    const isNewColor = changeColorArr.find(
                      x => x.theme_colorname === theme_colorname
                    )?.theme_colorvalue;

                    const key = `${theme_colorname}_${index}`;

                    return (
                      <CustomColorPicker
                        key={key}
                        className="p-2 m-1 b-1"
                        value={isNewColor}
                        onChange={e => handleChangeColor(e.target.value, theme_colorname)}
                        label={theme_colorname}
                        resetColorOnClick={() => handleChangeColor(theme_resetcolorvalue, theme_colorname)}
                      />
                    );
                  })}
              </div>
            </CustomCard>
          )}
        </CollapseComponent>
      </>
    );
  };

  return (
    <PageComponent
      isPageHeader={{
        isvisible: true
      }}
    >
      <ThemeSet />

      <>
        <CollapseComponent title="Color Palette" cardDataLoaded cardBodyHeight={200}>
          <RenderColorPalette />
        </CollapseComponent>

        <CollapseComponent title="Badges | Pill" cardDataLoaded>
          {RenderBadge}
        </CollapseComponent>

        <CollapseComponent title="Button" cardDataLoaded>
          <h6>Simple:-</h6>
          <div className="mb-3"> {RenderButton}</div>

          <h6>Outline:-</h6>
          <div>{RenderOutlineButton}</div>

          <h6>Block:-</h6>
          <CustomButton className="mr-3 mb-2" title="block button" block color="primary">
            primary
          </CustomButton>
          <CustomButton className="mr-3 mb-2" title="block outline button" block outline color="primary">
            primary
          </CustomButton>

          <h6>Disabled:-</h6>
          <CustomButton className="mr-3 mb-2" title="block button" disabled color="primary">
            primary
          </CustomButton>
          <CustomButton className="mr-3 mb-2" title="block outline button" disabled outline color="primary">
            primary
          </CustomButton>
        </CollapseComponent>
      </>
    </PageComponent>
  );
};

const mapStateToProps = state => ({
  themeArr: state.mainlayout?.themeArr
});
const mapDispatchToProps = { handleThemeArr };
ThemeDocs.displayName = 'ThemeDocs';
export default connect(mapStateToProps, mapDispatchToProps)(memo(ThemeDocs));
